import { getUniqueData } from "@/utils/uniqueParse";
import React, { useEffect, useState } from "react";
import AgencyCard from "../map/AgencyCard";
import { displacementBetween } from "@/utils/utilFunctions";

type Props = {
  locationName: string;
  cardata: CarData[];
  airportData: any;
  airportNameData: AirportNameData;
  locale: string;
};

const AllAgencyList = (props: Props) => {
  const [isUserAgentBot, setIsUserAgentBot] = useState(true);
  useEffect(() => {
    if (!/bot/i.test(navigator.userAgent)) setIsUserAgentBot(false);
  }, []);
  const [sliceLength, setSliceLength] = useState(4);
  const uniqueData = getUniqueData(props.cardata);

  const dynamictext = `Here is a list of all the car rental companies in and around ${props.locationName}:`;
  return (
    <div className="mt-5 lg:mt-[60px] bg-white px-[13px] py-[30px]">
      <div className="mx-auto max-w-7xl">
        <div className="flex justify-between w-full">
          <div className="space-y-[6px] lg:space-y-[10px] mb-[18px]">
            <h2 className="text-base font-extrabold lg:text-3xl text-my-dark-blue">
              All car rental companies in {props.locationName}
            </h2>
            <div className="space-y-6 text-sm font-normal text-my-light-blue lg:font-light lg:text-base">
              {dynamictext}
            </div>
          </div>
        </div>
        {isUserAgentBot && (
          <table className="w-full my-8">
            <tr>
              <th>Agency Name</th>
              <th>Agency Logo</th>
              <th>Distance From Airport</th>
              <th>Address</th>
              <th>Located At</th>
            </tr>
            {props.cardata &&
              (uniqueData || getUniqueData(props.cardata))
                .filter(
                  (a) =>
                    !a.agency.name.includes("Surprise Agency") &&
                    !a.agency.providerCode.includes("SPECIALRENTALCAR")
                )
                .map(
                  (
                    item: { provider: Provider; agency: Agency },
                    index: number
                  ) => {
                    const displacement = displacementBetween(
                      {
                        lat: props.airportData.lat,
                        lon: props.airportData.lng,
                      },
                      {
                        lat: item.agency.pickupLocation.lat,
                        lon: item.agency.pickupLocation.lon,
                      }
                    );

                    const generateDisplacementString = () => {
                      const displacementInMiles = displacement * 0.000621371;
                      if (props.locale === "en") {
                        if (displacementInMiles < 1) {
                          return `Less than 1 Mile`;
                        } else {
                          return `${Math.ceil(displacementInMiles)} Mile${
                            displacementInMiles < 2 ? "" : "s"
                          }`;
                        }
                      } else {
                        if (displacement < 1000) {
                          return `Less than 1 km`;
                        } else {
                          return `${Math.ceil(displacement / 1000)} km`;
                        }
                      }
                    };
                    return (
                      <tr key={index}>
                        <td>{item.agency.name}</td>
                        <td>
                          <img
                            src={
                              item.agency.name === "Surprise Agency"
                                ? "/rental-company-list/surprise.svg"
                                : `https://content.r9cdn.net/rimg/provider-logos/cars/h/${item.agency.icon}`
                            }
                            alt={item.agency.name}
                            width="200"
                            height="200"
                          />
                        </td>
                        <td>{generateDisplacementString()}</td>
                        <td>
                          {item.agency.pickupLocation.address === undefined
                            ? ""
                            : `${item.agency.pickupLocation.address},`}{" "}
                          {item.agency.pickupLocation.city === undefined
                            ? ""
                            : `${item.agency.pickupLocation.city}, ${item.agency.pickupLocation.cc}.`}
                        </td>
                        <td>
                          {item.agency.pickupLocation.locType ===
                          item.agency.pickupLocation.locCategory
                            ? item.agency.pickupLocation.locType
                            : item.agency.pickupLocation.locType +
                              ", " +
                              item.agency.pickupLocation.locCategory}
                        </td>
                      </tr>
                    );
                  }
                )}
          </table>
        )}
        {!isUserAgentBot && (
          <>
            <div className="grid grid-cols-1 py-4 lg:grid-cols-2 lg:gap-2">
              {props.cardata &&
                (uniqueData || getUniqueData(props.cardata))
                  .filter(
                    (a) =>
                      !a.agency.name.includes("Surprise Agency") &&
                      !a.agency.providerCode.includes("SPECIALRENTALCAR")
                  )
                  .slice(0, sliceLength)
                  .map(
                    (
                      item: { provider: Provider; agency: Agency },
                      index: number
                    ) => (
                      <AgencyCard
                        key={index}
                        agency={item.agency}
                        provider={item.provider}
                        airportData={props.airportNameData}
                        airportCoords={{
                          lat: props.airportData.lat,
                          lon: props.airportData.lng,
                        }}
                        locale={props.locale}
                      />
                    )
                  )}
            </div>
            {props.cardata &&
              (uniqueData || getUniqueData(props.cardata)).length > 4 && (
                <div className="flex justify-end w-full">
                  <p
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      if (sliceLength === 4) setSliceLength(uniqueData.length);
                      else setSliceLength(4);
                    }}
                    className="cursor-pointer select-none w-fit text-[#004DE3] hover:underline"
                  >
                    {`Show ${
                      sliceLength === 4 && (props.cardata ?? [])?.length > 4
                        ? "More"
                        : "Less"
                    } Providers `}
                  </p>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default AllAgencyList;
